import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Menu, Bell, LogOut, Download, ShieldCheck, Sun, Moon, Monitor, Check } from "lucide-react";

let deferredPrompt = null;
window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredPrompt = e; });

export function Topbar({ onMenu }) {
  const { user, logout } = useAuth();
  const { theme, resolved, setTheme } = useTheme();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [canInstall, setCanInstall] = useState(!!deferredPrompt);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/notifications", { params: { unread_only: true } });
        setUnread(data.length);
      } catch {}
    };
    load();
    const t = setInterval(load, 30000);
    const handler = () => setCanInstall(true);
    window.addEventListener("beforeinstallprompt", handler);
    return () => { clearInterval(t); window.removeEventListener("beforeinstallprompt", handler); };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    deferredPrompt = null;
    setCanInstall(false);
  };

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-background/70 px-4 backdrop-blur-xl sm:px-6">
      <button className="lg:hidden" onClick={onMenu} data-testid="menu-toggle"><Menu className="h-5 w-5" /></button>
      <div className="flex items-center gap-2">
        <span className="rounded-sm bg-primary/10 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider text-primary">{(user?.role || "").replace(/_/g, " ")}</span>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {canInstall && (
          <Button variant="outline" size="sm" onClick={install} data-testid="install-btn" className="hidden sm:flex">
            <Download className="mr-1.5 h-4 w-4" /> Install App
          </Button>
        )}
        <Button variant="ghost" size="icon" className="relative" onClick={() => navigate("/notifications")} data-testid="notif-btn">
          <Bell className="h-5 w-5" />
          {unread > 0 && <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">{unread > 99 ? "99+" : unread}</span>}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="theme-toggle">
              {resolved === "dark" ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuLabel>Theme</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {[["light", "Light", Sun], ["dark", "Dark", Moon], ["system", "System", Monitor]].map(([val, label, Icon]) => (
              <DropdownMenuItem key={val} onClick={() => setTheme(val)} data-testid={`theme-${val}`}>
                <Icon className="mr-2 h-4 w-4" />{label}
                {theme === val && <Check className="ml-auto h-4 w-4 text-primary" />}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded-sm p-1 hover:bg-secondary" data-testid="user-menu">
              <Avatar className="h-8 w-8"><AvatarImage src={user?.picture} /><AvatarFallback className="bg-sidebar text-xs text-white">{initials}</AvatarFallback></Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="font-medium">{user?.name}</div>
              <div className="text-xs font-normal text-muted-foreground">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled><ShieldCheck className="mr-2 h-4 w-4" />{(user?.permissions || []).includes("*") ? "Full access" : `${(user?.permissions || []).length} permissions`}</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="logout-btn"><LogOut className="mr-2 h-4 w-4" /> Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
